import dayjs from 'dayjs';
import isUndefined from 'lodash/isUndefined';
import { createSelector } from 'reselect';
import { currentAccountConsumerAgeSelector } from 'store/account/selectors';
import kebabCase from 'lodash/kebabCase';
import { RootState } from 'store/types';
import { HEAD_CIRCUMFERENCE_VITAL_SERVICE_NAME, WEIGHT_VITAL_SERVICE_NAME } from './constants';
import {
  getBloodTypeVitals,
  normalizedBloodPressure,
  normalizedCoreVitals,
  normalizedHistoricalVitals
} from './mappers';
import { VitalsBlock } from './types';

/**
 *  Recent Vitals Selectors
 */

export const recentVitalsListSelector = (state: RootState) => state.vitals.recentVitals;

export const recentVitalsLoadingSelector = createSelector(
  recentVitalsListSelector,
  vitals => vitals.isFetching
);

export const recentVitalsErrorSelector = createSelector(
  recentVitalsListSelector,
  vitals => vitals.error
);

export const recentVitalsRequestStateSelector = createSelector(
  recentVitalsListSelector,
  vitals => vitals.requestState
);

export const recentVitalsRawDataSelector = createSelector(
  recentVitalsListSelector,
  vitals => vitals.data
);

export const recentVitalsDataSelector = createSelector(recentVitalsListSelector, vitals =>
  normalizedCoreVitals(vitals.data)
);

export const bloodTypeSelector = createSelector(recentVitalsListSelector, vitals =>
  getBloodTypeVitals(vitals.data)
);

export const bloodPressureSelector = createSelector(recentVitalsListSelector, vitals =>
  normalizedBloodPressure(vitals.data)
);

/**
 *
 * Active Visit Vitals Selectors
 */
export const activeVisitVitalsSelector = (state: RootState) => state.vitals.activeVisitVitals;

export const activeVisitVitalsLoadingSelector = createSelector(
  activeVisitVitalsSelector,
  vitals => vitals.isFetching
);

export const activeVisitVitalsErrorSelector = createSelector(
  activeVisitVitalsSelector,
  vitals => vitals.error
);

export const activeVisitVitalsDataSelector = createSelector(activeVisitVitalsSelector, vitals =>
  normalizedCoreVitals(vitals.data)
);

/**
 *
 * Historical Vital Selectors
 */

export const historicalVitalsSelector = (state: RootState) => state.vitals.historicalVitals;

export const historicalVitalsLoadingSelector = createSelector(
  historicalVitalsSelector,
  vitals => vitals.isFetching
);

export const historicalVitalsErrorSelector = createSelector(
  historicalVitalsSelector,
  vitals => vitals.error
);

export const historicalVitalsDataSelector = createSelector(
  historicalVitalsSelector,
  vitals => vitals.data
);

export const historicalVitalCurrentTypeFetchSelector = createSelector(
  historicalVitalsSelector,
  vitals => vitals.currentTypeFetch
);

export const historicalVitalsChartDataSelector = createSelector(
  [recentVitalsRawDataSelector, historicalVitalCurrentTypeFetchSelector],
  (vitals, type) => {
    const filteredData = vitals.filter(item => item.name === type);
    return normalizedHistoricalVitals(filteredData);
  }
);

export const vitalsLastUpdatedSelector = createSelector(
  [recentVitalsDataSelector],
  (recentVitalsData: VitalsBlock[]) => {
    const maxDate = recentVitalsData
      .filter(vital => vital.date !== '')
      .map(vital => vital.date)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

    if (isUndefined(maxDate)) return '';

    return dayjs(maxDate).format(`M/D/YYYY`);
  }
);

export const filterVitalsByAge = (age: number, vitals: VitalsBlock[]) => {
  return vitals
    .map((item: VitalsBlock) => {
      // Show pounds and ounces if under the age of 3
      if (age > 3 && item.serviceName === WEIGHT_VITAL_SERVICE_NAME) {
        return { ...item, valueUnits: [item.valueUnits[0]] };
      }
      return item;
    })
    .filter((item: VitalsBlock) => {
      // Only show head circumference if under the age of 2
      if (age > 2) {
        return item.serviceName !== HEAD_CIRCUMFERENCE_VITAL_SERVICE_NAME;
      }
      return item;
    });
};

export const recentVitalsSelector = createSelector(
  [currentAccountConsumerAgeSelector, recentVitalsDataSelector],
  (age, recentVitalsData) => {
    return filterVitalsByAge(age, recentVitalsData);
  }
);

export const encounterVitalsSelector = createSelector(
  [currentAccountConsumerAgeSelector, activeVisitVitalsDataSelector],
  (age, encounterVitalsData) => {
    return filterVitalsByAge(age, encounterVitalsData);
  }
);

/**
 * Share Vitals
 */
export const shareVitalsSelector = (state: RootState) => state.vitals.shareVitals;

export const shareVitalsSendingSelector = createSelector(
  shareVitalsSelector,
  vitals => vitals.isSendingVitals
);

export const shareVitalsDataSelector = createSelector(
  shareVitalsSelector,
  vitals => vitals.sharedVitalsData
);

/**
 * Download Vitals
 */
export const vitalsDownloadsSelector = (state: RootState) => state.vitals.downloadVitals;

export const vitalsDownloadingSelector = createSelector(
  vitalsDownloadsSelector,
  vitals => vitals.isDownloadingVitals
);

export const vitalsDownloadsByEncounterSelector = (state: RootState) =>
  state.vitals.downloadVisitVitals.vitalsDownloadsByEncounter;

export const searchFormattedVitals = createSelector(recentVitalsSelector, data => {
  return data.map(item => {
    return {
      title: item?.title,
      date: item?.date,
      category: 'Vitals',
      icon: 'VitalsIcon',
      keywords: ['vitals'],
      data: item,
      navWebPath: `/u/health-record/vitals/${item?.serviceName}/${kebabCase(
        item?.title?.toLowerCase()
      )}`
    };
  });
});
